import type { AriaAttributes } from 'react'
import type { SortingState } from '@tanstack/react-table'

/**
 * A column's sort state — `false` unsorted, `'asc'`/`'desc'` sorted. Resolved via
 * `resolveSortDirection` (below) — the preset does NOT call the mutable
 * `column.getIsSorted()` in the header cell (see that helper's doc).
 */
export type SortDirection = false | 'asc' | 'desc'

/**
 * Resolve a column's `SortDirection` from the engine's `SortingState` — a pure
 * mirror of `column.getIsSorted()` that reads the state VALUE rather than calling
 * the mutable engine method during render.
 *
 * The distinction is load-bearing under the React Compiler: `getIsSorted()` reads
 * `table.getState().sorting` off the referentially-stable `table`, which the
 * compiler cannot see change, so a header memoized on `table` alone freezes its
 * sort glyph / `aria-sort` while the body re-sorts. Deriving from the reactive
 * `SortingState` instead lets the caller key its header memo on the state, so the
 * indicator tracks the active sort. See `data-table.tsx`'s header cells.
 */
export const resolveSortDirection = (sorting: SortingState, columnId: string): SortDirection => {
  const entry = sorting.find((sort) => sort.id === columnId)
  if (entry === undefined) {
    return false
  }

  return entry.desc ? 'desc' : 'asc'
}

/**
 * Maps a column's `SortDirection` to its `aria-sort` token — the accessible sort channel
 * on the `columnheader`, kept separate from the decorative sort glyph. `'none'` at rest.
 */
export const toAriaSort = (sort: SortDirection): AriaAttributes['aria-sort'] => {
  if (sort === 'asc') {
    return 'ascending'
  }
  if (sort === 'desc') {
    return 'descending'
  }

  return 'none'
}
